<template>
  <div
    class="agent-item p-3 rounded-[8px] bg-white border border-gray-200 flex flex-row gap-2"
  >
    <div
      class="px-3 py-1 flex flex-row items-center gap-3 min-w-[429px] max-2xl:min-w-[330px]"
    >
      <div class="agent-avatar">
        <thumbnail
          :src="agent.thumbnail"
          class="columns"
          :username="agent.name"
          size="32px"
          :status="agent.availability_status"
        />
      </div>
      <div class="space-y-1">
        <div>
          <span class="text-sm text-primary mr-2 rtl:ml-2 rtl:mr-0">
            {{ agent.name }}
          </span>
          <span
            v-if="agent.operation_bot"
            class="semantic-tag small tag-mute-01 text-xs"
          >
            {{ $t('SEARCH.BOT_LABEL') }}
          </span>
        </div>
        <div
          class="text-gray-500 text-xs overflow-hidden text-ellipsis max-w-[260px]"
        >
          <span>#{{ agent.id }}</span>
          <span
            class="mx-1 inline-block text-[30px] text-slate-500 align-text-top"
          >
            ·
          </span>
          <span>{{ agent.email }}</span>
        </div>
      </div>
    </div>
    <div class="flex flex-row flex-1 items-center gap-2">
      <div
        v-if="agentCustomAttributes.length"
        class="flex flex-row items-center gap-1"
      >
        <span
          v-for="attr in agentCustomAttributes[0]"
          :key="attr.attribute_key"
          class="semantic-tag tag-mute-01 text-sm whitespace-nowrap"
        >
          {{ attr.attribute_display_name }}
        </span>
        <div
          v-if="Object.keys(agentCustomAttributes[1]).length"
          class="relative"
        >
          <span
            class="more-custom-attributes-anchor semantic-tag tag-mute-01 text-sm whitespace-nowrap z-0 relative"
          >
            +{{ Object.keys(agentCustomAttributes[1]).length }}
          </span>
          <div class="absolute custom-attributes-more shadow z-[10]">
            <div class="text-xs mb-1">
              {{ $t('AGENT_MGMT.FORM.AGENT_SKILLS.LABEL') }}:
            </div>
            <div class="attributes relative z-10">
              <span
                v-for="attr in agentCustomAttributes[1]"
                :key="attr.attribute_key"
                class="semantic-tag tag-mute-01 text-sm whitespace-nowrap"
              >
                {{ attr.attribute_display_name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-row items-center justify-end min-w-[429px]">
      <div class="min-w-[155px] flex-auto flex items-center justify-center">
        <span
          class="semantic-tag text-sm whitespace-nowrap block"
          :class="roleColor(agent.role)"
        >
          {{ translatedRole(agent.role) }}
        </span>
      </div>
      <!-- QM §4.6, §5.1.2 — the per-agent cap at a glance, so the queue owner does not
           have to open each agent to see who is capped. -->
      <div
        v-if="isQueueManagementEnabled"
        class="text-sm min-w-[110px] flex items-center justify-center"
        :title="$t('AGENT_MGMT.FORM.CAPACITY_CAP.LABEL')"
      >
        <span class="text-gray-500 mr-1 rtl:ml-1 rtl:mr-0">
          {{ $t('AGENT_MGMT.LIST.CAPACITY_CAP') }}:
        </span>
        <span v-if="agent.capacity_cap" class="text-primary">
          {{ agent.capacity_cap }}
        </span>
        <span v-else class="text-gray-400">
          {{ $t('AGENT_MGMT.LIST.CAPACITY_UNLIMITED') }}
        </span>
      </div>
      <div class="text-sm min-w-[130px] flex items-center justify-center">
        <div
          v-if="agent.two_factor_type"
          class="flex flex-row gap-2 items-center justify-between text-green-500"
        >
          <span class="material-symbols-outlined">verified_user</span>
          <span>{{ $t('AGENT_MGMT.LIST.TWO_FACTOR_ENABLED') }}</span>
        </div>
        <div
          v-if="!agent.two_factor_type"
          class="text-[#d97706] flex flex-row gap-2 items-center"
        >
          <span class="material-symbols-outlined">privacy_tip</span>
          <span>{{ $t('AGENT_MGMT.LIST.TWO_FACTOR_DISABLED') }}</span>
        </div>
      </div>
      <div class="min-w-[130px] flex items-center justify-center">
        <div
          v-if="agent.confirmed"
          class="text-green-500 flex flex-row gap-2 items-center"
        >
          <svg
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.13825 19.7305L5.35375 16.723L1.96525 15.9805L2.296 12.4922L0 9.86525L2.296 7.23825L1.96525 3.75L5.35375 3.0075L7.13825 0L10.3268 1.35375L13.5153 0L15.2998 3.0075L18.6883 3.75L18.3575 7.23825L20.6535 9.86525L18.3575 12.4922L18.6883 15.9805L15.2998 16.723L13.5153 19.7305L10.3268 18.3768L7.13825 19.7305ZM7.77675 17.8153L10.3268 16.7345L12.9075 17.8153L14.3268 15.4152L17.0768 14.7845L16.8268 11.9653L18.6768 9.86525L16.8268 7.7345L17.0768 4.91525L14.3268 4.31525L12.8768 1.91525L10.3268 2.996L7.746 1.91525L6.32675 4.31525L3.57675 4.91525L3.82675 7.7345L1.97675 9.86525L3.82675 11.9653L3.57675 14.8152L6.32675 15.4152L7.77675 17.8153ZM9.27675 13.069L14.5805 7.76525L13.5268 6.68075L9.27675 10.9307L7.12675 8.8115L6.073 9.86525L9.27675 13.069Z"
              fill="currentColor"
            />
          </svg>
          <span>{{ $t('AGENT_MGMT.LIST.VERIFIED') }}</span>
        </div>
        <div
          v-if="!agent.confirmed"
          class="text-[#d97706] flex flex-row gap-2 items-center"
        >
          <svg
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.2998 3.00781L18.6885 3.75L18.3574 7.23828L20.6543 9.86523L18.3574 12.4922L18.6885 15.9805L15.2998 16.7227L13.5156 19.7305L10.3271 18.377L7.13867 19.7305L5.35449 16.7227L1.96582 15.9805L2.29688 12.4922L0 9.86523L2.29688 7.23828L1.96582 3.75L5.35449 3.00781L7.13867 0L10.3271 1.35352L13.5156 0L15.2998 3.00781ZM10.3271 2.99609L7.74609 1.91504L6.32715 4.31543L3.57715 4.91504L3.82715 7.73438L1.97754 9.86523L3.82715 11.9648L3.57715 14.8154L6.32715 15.415L7.77734 17.8154L10.3271 16.7344L12.9082 17.8154L14.3271 15.415L17.0771 14.7842L16.8271 11.9648L18.6768 9.86523L16.8271 7.73438L17.0771 4.91504L14.3271 4.31543L12.877 1.91504L10.3271 2.99609ZM10.1348 12.4805C10.3635 12.4805 10.5552 12.5582 10.71 12.7129C10.8648 12.8677 10.9423 13.0593 10.9424 13.2881C10.9424 13.5169 10.8648 13.7084 10.71 13.8633C10.5551 14.0179 10.3636 14.0957 10.1348 14.0957C9.90599 14.0957 9.71437 14.0179 9.55957 13.8633C9.40483 13.7085 9.32715 13.5168 9.32715 13.2881C9.32718 13.0593 9.40477 12.8677 9.55957 12.7129C9.71437 12.5581 9.90599 12.4805 10.1348 12.4805ZM10.8848 10.6924H9.38477V5.86523H10.8848V10.6924Z"
              fill="currentColor"
            />
          </svg>
          <span>{{ $t('AGENT_MGMT.LIST.NO_VERIFIED') }}</span>
        </div>
      </div>
      <div class="w-fit min-w-[68px] relative flex items-center justify-center">
        <ul class="m-0">
          <li>
            <button
              class="more-options-anchor w-[44px] h-[44px] flex items-center justify-center button-ghost text-gray-500 !border border-gray-200"
              :disabled="agent.operation_bot"
              style="border: inherit"
              @click="openMenu"
              @blur="isOpenMenu = false"
            >
              <span class="material-symbols-outlined">more_vert</span>
            </button>
            <ul
              v-if="!agent.operation_bot && isOpenMenu"
              class="more-options-container flex flex-col items-center absolute right-0 rtl:left-0 rtl:right-[unset] rounded-[8px] bg-white border border-gray-200"
              :class="menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'"
              @mousedown.prevent
            >
              <li v-for="action in actions" :key="action.name">
                <div
                  class="py-2 px-3 w-[190px] text-gray-600 text-base flex items-center gap-1"
                  @click="action.onClick"
                >
                  <span class="material-symbols-outlined text-icon-primary">
                    {{ action.icon }}
                  </span>
                  <span>{{ action.name }}</span>
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Thumbnail from '../../../../components/widgets/Thumbnail.vue';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
// QM §4.6 — reused purely for its `isQueueManagementEnabled` computed.
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';

export default {
  components: {
    Thumbnail,
  },
  mixins: [globalConfigMixin, hasPermissionMixin, queueSupervisionMixin],
  props: {
    agent: {
      type: Object,
      required: true,
    },
    openEditPopup: {
      type: Function,
      required: true,
    },
    openDeletePopup: {
      type: Function,
      required: true,
    },
    openDisconnectPopup: {
      type: Function,
      required: true,
    },
    elementWidth: {
      type: Number,
      require: false,
      default: 1300,
    },
    elementHeight: {
      type: Number,
      require: false,
      default: null,
    },
  },
  data() {
    return {
      isOpenMenu: false,
      menuOpensUpward: false,
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      customAttributes: 'attributes/getAttributesByModel',
    }),
    showDisconnect() {
      return this.hasEditPermission('Disconnect Agents');
    },
    actions() {
      let items = [];
      if (this.showEditAction) {
        items = [
          ...items,
          {
            name: this.$t('AGENT_MGMT.EDIT.BUTTON_TEXT'),
            icon: 'edit',
            onClick: () => this.openModal('openEditPopup'),
          },
        ];
      }
      if (this.showDisconnect) {
        items = [
          ...items,
          {
            name: this.$t('AGENT_MGMT.DISCONNECT.BUTTON_TEXT'),
            icon: 'person_remove',
            onClick: () => this.openModal('openDisconnectPopup'),
          },
        ];
      }
      if (this.showDeleteAction) {
        items = [
          ...items,
          {
            name: this.$t('AGENT_MGMT.DELETE.BUTTON_TEXT'),
            icon: 'delete',
            onClick: () => this.openModal('openDeletePopup'),
          },
        ];
      }

      return items;
    },
    showEditAction() {
      if (this.currentUserId === this.agent.id) {
        return false;
      }
      return this.hasEditPermission('Agents');
    },
    showDeleteAction() {
      if (this.currentUserId === this.agent.id) {
        return false;
      }

      if (!this.agent.confirmed) {
        return this.hasDeletePermission('Agents');
      }

      if (this.agent.role === 'administrator') {
        return (
          this.verifiedAdministrators.length !== 1 &&
          this.hasDeletePermission('Agents')
        );
      }

      return this.hasDeletePermission('Agents');
    },
    verifiedAdministrators() {
      return this.agentList.filter(
        agent => agent.role === 'administrator' && agent.confirmed
      );
    },
    agentCustomAttributes() {
      const agentAttrs = this.agent.custom_attributes;
      const all = this.customAttributes('agent_attribute')
        .filter(attr => attr.attribute_key in agentAttrs)
        .map(attr => ({ ...attr, value: agentAttrs[attr.attribute_key] }));

      let sliceNumber = 2;
      if (this.elementWidth <= 1024) {
        sliceNumber = 1;
      } else if (this.elementWidth <= 1300) {
        sliceNumber = 2;
      }

      return [all.slice(0, sliceNumber), all.slice(sliceNumber)];
    },
  },
  methods: {
    translatedRole(role) {
      const roleKey = `AGENT_MGMT.AGENT_TYPES.${role && role.toUpperCase()}`;
      const translation = this.$t(roleKey);
      return translation !== roleKey ? translation : role;
    },
    roleColor(role) {
      const colors = {
        'General Manager': 'tag-primary-01',
        Admin: 'tag-primary-02',
        Marketing: 'tag-primary-06',
        'Shift Manager': 'tag-primary-04',
        Agent: 'tag-primary-03',
      };
      return colors[role] || 'tag-primary-03';
    },
    openMenu(event) {
      if (!this.isOpenMenu) {
        const MENU_HEIGHT = 120;
        const rect = event.currentTarget.getBoundingClientRect();
        this.menuOpensUpward = rect.bottom + MENU_HEIGHT > window.innerHeight;
      }
      this.isOpenMenu = !this.isOpenMenu;
    },
    openModal(methodName) {
      this.isOpenMenu = false;
      this[methodName](this.agent);
    },
  },
};
</script>
<style lang="scss" scoped>
li {
  list-style: none;
  cursor: pointer;

  ul {
    @apply block z-[999];

    li {
      @apply hover:bg-[#F9FAFB] hover:text-[#4B5563];
    }
  }
}

.agent-item:first-child,
.agent-item:nth-child(2) {
  .custom-attributes-more {
    left: -65px;
    top: calc(100% + 12px);
    bottom: unset;

    &::after {
      top: -6px;
      bottom: auto;
      bottom: initial;
      box-shadow: none;
      border-left: 1px solid #f3f3f3;
      border-top: 1px solid #f3f3f3;
    }
  }
}

.custom-attributes-more {
  @apply hidden bg-white py-1 px-2 rounded-[8px];
  left: -65px;
  bottom: calc(100% + 12px);

  &::after {
    content: '';
    @apply absolute w-3 h-3 bg-white z-0;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.1);
  }

  .attributes {
    @apply flex gap-1 w-[150px] z-[2] flex-wrap;
  }
}

.more-custom-attributes-anchor {
  cursor: pointer;
  &:hover {
    background: #47556929;
    ~ .custom-attributes-more {
      @apply block;
    }
  }
}
</style>
